/* @flow */

import { fetchGraphql, fetchQC } from '../utils/ajax';
import type { GraphqlRequest, CallApi } from '../utils/ajax';

export type GraphqlVariables = {
  projectId: ?[string],
  links: Object,
  submittedFileTypes?: [string] }

export type State = {
  projectId: string,
  casesWithClinical: {
    data: { caseCount: number, count: number },
    isFetching: boolean
  },
  casesWithBiospecimen: {
    data: { caseCount: number, count: number },
    isFetching: boolean
  },
  casesWithData: {
    data: { caseCount: number, count: number },
    isFetching: boolean
  },
  filesUploaded: {
    data: { processed: number, count: number, validatedIds: Array<string>, manifestIds: Array<string> },
    isFetching: boolean
  }
}
export type Action = { type: string, payload: {data: any}, meta: {projectId?: string, links?: Object, submittedFileTypes?: Array<string>} }

export const WIDGETS_REQUEST = 'gdc/WIDGETS_REQUEST';

export const CASES_W_CLINICAL_SUCCESS = 'gdc/CASES_W_CLINICAL_SUCCESS';
export const CASES_W_CLINICAL_FAILURE = 'gdc/CASES_W_CLINICAL_FAILURE';

export const CASES_W_BIOSPECIMEN_SUCCESS = 'gdc/CASES_W_BIOSPECIMEN_SUCCESS';
export const CASES_W_BIOSPECIMEN_FAILURE = 'gdc/CASES_W_BIOSPECIMEN_FAILURE';

export const CASES_W_DATA_SUCCESS = 'gdc/CASES_W_DATA_SUCCESS';
export const CASES_W_DATA_FAILURE = 'gdc/CASES_W_DATA_FAILURE';

export const FILES_UPLOADED_SUCCESS = 'gdc/FILES_UPLOADED_SUCCESS';
export const FILES_UPLOADED_FAILURE = 'gdc/FILES_UPLOADED_FAILURE';

export const QC_ERRORS_SUCCESS = 'gdc/QC_ERRORS_SUCCESS';
export const QC_ERRORS_FAILURE = 'gdc/QC_ERRORS_FAILURE';

function queryCasesWithClinical({ projectId, links }: GraphqlVariables): GraphqlRequest {
  return {
    query: `query CasesWithClinical($projectId: [String]) {
      caseCount: _case_count(project_id: $projectId)
      ${(links.clinicalLinks.withLink || []).map(l => `${l}: _case_count(with_links: ["${l}"] project_id: $projectId)`).join()}
      ${(links.clinicalLinks.withPathTo || []).map(l => `${l}: _case_count(with_path_to: [{type: "${l}"}] project_id: $projectId)`).join()}
      count: _case_count(with_links_any: ["${links.clinicalLinks.withLink.join('","')}"] project_id: $projectId)
    }`,
    variables: { projectId },
  };
}

function queryCasesWithBiospecimen({ projectId, links }: GraphqlVariables): GraphqlRequest {
  return {
    query: `query CasesWithBiospecimen($projectId: [String]) {
      caseCount: _case_count(project_id: $projectId)
      count: _case_count(with_links: ["samples"] project_id: $projectId)
      ${links.biospecimenLinks.map(l => `${l}: _case_count(with_path_to: [{type: "${l}"}] project_id: $projectId)`).join()}
    }`,
    variables: { projectId },
  };
}

function queryCasesWithData({ projectId, links }: GraphqlVariables): GraphqlRequest {
  return {
    query: `query CasesWithData($projectId: [String]) {
      caseCount: _case_count(project_id: $projectId)
      count: _case_count(with_path_to_any: [${links.dataBundleLinks.map(l => `{type: "${l}"}`)}] project_id: $projectId)
      ${links.dataBundleLinks.map(l => `${l}: _case_count(with_path_to: [{type: "${l}"}] project_id: $projectId)`).join()}
    }`,
    variables: { projectId },
  };
}

function queryFilesUploaded({ projectId, submittedFileTypes }: GraphqlVariables): GraphqlRequest {
  return {
    query: `query FilesUploaded($projectId: [String]) {
      ${(submittedFileTypes || []).map(t => `${t}_count: _${t}_count(project_id: $projectId state: ["validated"])`)}
      ${(submittedFileTypes || []).map(t => `${t}_validated_ids: ${t}(file_state: ["validated"] state: ["validated"] project_id: $projectId first: 0 ) { id }`)}
      ${(submittedFileTypes || []).map(t => `${t}_manifest_ids: ${t}(file_state: ["registered", "deleted", "error"] state: ["validated"] project_id: $projectId first: 0 ) { id }`)}
    }`,
    variables: { projectId },
  };
}

export function fetchCasesWithClinical(props: GraphqlVariables): CallApi {
  return fetchGraphql(
    [{ type: WIDGETS_REQUEST, meta: { projectId: props.projectId } },
     CASES_W_CLINICAL_SUCCESS, CASES_W_CLINICAL_FAILURE],
    queryCasesWithClinical(props),
  );
}

export function fetchCasesWithBiospecimen(props: GraphqlVariables): CallApi {
  return fetchGraphql(
    [{ type: WIDGETS_REQUEST, meta: { projectId: props.projectId } },
     CASES_W_BIOSPECIMEN_SUCCESS, CASES_W_BIOSPECIMEN_FAILURE],
    queryCasesWithBiospecimen(props),
  );
}

export function fetchCasesWithData(props: GraphqlVariables): CallApi {
  return fetchGraphql(
    [{ type: WIDGETS_REQUEST, meta: { projectId: props.projectId } },
     CASES_W_DATA_SUCCESS, CASES_W_DATA_FAILURE],
    queryCasesWithData(props),
  );
}

export function fetchFilesUploaded(props: GraphqlVariables): CallApi {
  return fetchGraphql(
    [{ type: WIDGETS_REQUEST, meta: { projectId: props.projectId } },
     { type: FILES_UPLOADED_SUCCESS, meta: { links: props.links, submittedFileTypes: props.submittedFileTypes } },
     FILES_UPLOADED_FAILURE],
    queryFilesUploaded(props),
  );
}

export function fetchQCErrors({ program, project }) {
  return fetchQC({
    types: [{ type: WIDGETS_REQUEST, meta: { projectId: project } }, QC_ERRORS_SUCCESS, QC_ERRORS_FAILURE],
    endpoint: 'summary',
    program,
    project,
  });
}

const initialData = {
  casesWithClinical: {
    data: { caseCount: 0, count: 0 },
    isFetching: true,
  },
  casesWithBiospecimen: {
    data: { caseCount: 0, count: 0 },
    isFetching: true,
  },
  casesWithData: {
    data: { caseCount: 0, count: 0 },
    isFetching: true,
  },
  filesUploaded: {
    data: {
      processed: 0, count: 0, validatedIds: [], manifestIds: [],
    },
    isFetching: true,
  },
  qcErrors: {
    data: { CRITICAL: 0, WARNING: 0 },
    isFetching: true,
  },
};

export default function widgets(state: State = {
  projectId: '',
  ...initialData,
}, action: Action): State {
  switch (action.type) {
    case WIDGETS_REQUEST:
      return {
        projectId: (action.meta.projectId || [])[0],
        ...((action.meta.projectId || [])[0] === state.projectId
        ? {
          ...state,
          casesWithClinical: { ...state.casesWithClinical, isFetching: true },
          casesWithBiospecimen: { ...state.casesWithBiospecimen, isFetching: true },
          casesWithData: { ...state.casesWithData, isFetching: true },
          filesUploaded: { ...state.filesUploaded, isFetching: true },
          qcErrors: { ...state.qcErrors, isFetching: true },
        }
        : initialData),
      };
    case CASES_W_CLINICAL_SUCCESS:
      return {
        ...state,
        casesWithClinical: {
          isFetching: false,
          data: action.payload.data,
        },
      };
    case CASES_W_BIOSPECIMEN_SUCCESS:
      return {
        ...state,
        casesWithBiospecimen: {
          isFetching: false,
          data: action.payload.data,
        },
      };
    case CASES_W_DATA_SUCCESS:
      return {
        ...state,
        casesWithData: {
          isFetching: false,
          data: action.payload.data,
        },
      };
    case FILES_UPLOADED_SUCCESS:
      const { data } = action.payload;
      return {
        ...state,
        filesUploaded: {
          isFetching: false,
          data: {
            processed: (action.meta.submittedFileTypes || []).reduce((sum, t) => sum + data[`${t}_processed`], 0),
            count: (action.meta.submittedFileTypes || []).reduce((sum, t) => sum + data[`${t}_count`], 0),
            validatedIds: (action.meta.submittedFileTypes || []).reduce((acc, t) => [...acc, ...data[`${t}_validated_ids`].map(o => o.id)], []),
            manifestIds: (action.meta.submittedFileTypes || []).reduce((acc, t) => [...acc, ...data[`${t}_manifest_ids`].map(o => o.id)], []),
          },
        },
      };
    case QC_ERRORS_SUCCESS:
      return {
        ...state,
        qcErrors: {
          isFetching: false,
          data: {
            ...state.qcErrors.data,
            ...action.payload.data,
          },
        },
      };
    case CASES_W_CLINICAL_FAILURE:
      return {
        ...state,
        casesWithClinical: initialData.casesWithClinical,
      };
    case CASES_W_BIOSPECIMEN_FAILURE:
      return {
        ...state,
        casesWithBiospecimen: initialData.casesWithBiospecimen,
      };
    case CASES_W_DATA_FAILURE:
      return {
        ...state,
        casesWithData: initialData.casesWithData,
      };
    case FILES_UPLOADED_FAILURE:
      return {
        ...state,
        filesUploaded: initialData.filesUploaded,
      };
    case QC_ERRORS_FAILURE:
      return {
        ...state,
        qcErrors: initialData.qcErrors,
      };
    default:
      return state;
  }
}
